import type { PersonDetailWire, PersonDeiWire } from '@releve/shared';
import {
  PERSON_LOCATION_TYPES,
  PERSON_PROFICIENCY_LEVELS,
} from '@releve/shared';

// The person vocabularies live in `@releve/shared` (the write schemas and the
// filter document read them too); this module re-exports them so the web's
// existing `@/types/people` imports keep working.
export { PERSON_TYPES, PERSON_STATUSES } from '@releve/shared';
export type { PersonType, PersonStatus } from '@releve/shared';

export interface Person {
  id: string;
  code: string;
  organization_id: string;

  // Core Identity
  first_name: string;
  last_name: string;
  middle_name?: string | null;
  preferred_name?: string | null;
  display_name?: string | null;

  // Contact Information
  email?: string | null;
  secondary_email?: string | null;
  phone?: string | null;
  mobile_phone?: string | null;
  work_phone?: string | null;

  // Address handled via person_locations table

  // Professional/Organization Info
  company?: string | null;
  job_title?: string | null;
  department?: string | null;

  // Classification
  person_type?: string[] | null;

  // Business marker — when true, this person is also a business/vendor and
  // linked_business_id points to their own `businesses` row (SCRUM-161).
  is_business?: boolean;
  linked_business_id?: string | null;

  // Optional Link to Auth
  auth_user_id?: string | null;
  has_system_access: boolean;

  // Additional Info
  profile_photo_url?: string | null;
  profile_photo_small?: string | null;
  profile_photo_medium?: string | null;
  profile_photo_large?: string | null;
  linkedin_url?: string | null;
  website?: string | null;
  bio?: string | null;
  notes?: string | null;
  tags?: string[] | null;

  // Status
  status: string;

  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export interface Discipline {
  id: string;
  organization_id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  color: string;
  icon?: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Skill {
  id: string;
  organization_id: string;
  name: string;
  category?: string | null;
  description?: string | null;
  color: string;
  is_active: boolean;
  created_at: string;
}

export interface PersonDiscipline {
  id: string;
  // Optional: the detail embeds (`PersonDetailWire`) select these rows without
  // their parent id / timestamps, and new form rows have neither yet.
  person_id?: string;
  discipline_id: string;
  context_type: string;
  context_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_primary: boolean;
  notes?: string | null;
  created_at?: string;
  discipline?: Discipline;
}

export interface PersonSkill {
  id: string;
  // Optional: the detail embeds (`PersonDetailWire`) select these rows without
  // their parent id / timestamps, and new form rows have neither yet.
  person_id?: string;
  skill_id: string;
  proficiency_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert' | null;
  years_experience?: number | null;
  last_used?: string | null;
  notes?: string | null;
  created_at?: string;
  skill?: Skill;
}

/**
 * A `person_dei` row (GDPR Art. 9 special-category data). Only an org ADMIN
 * ever receives one — `PersonDetailWire.dei` is `null` for everyone else.
 */
export type PersonDEI = PersonDeiWire;

export interface PersonEmergencyContact {
  id: string;
  person_id: string;
  contact_name: string;
  relationship?: string | null;
  phone?: string | null;
  email?: string | null;
  is_primary: boolean;
  created_at: string;
}

export interface BusinessContact {
  id: string;
  business_id: string;
  person_id: string;
  role?: string | null;
  is_primary: boolean;
  start_date?: string | null;
  end_date?: string | null;
  created_at: string;
  person?: Person;
  business?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface ProjectTeamMember {
  id: string;
  project_id: string;
  person_id: string;
  role?: string | null;
  allocation_percentage?: number | null;
  hourly_rate?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  created_at: string;
  person?: Person;
  project?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface PersonInteraction {
  id: string;
  person_id: string;
  interaction_type: string;
  interaction_date: string;
  subject?: string | null;
  notes?: string | null;
  related_entity_type?: string | null;
  related_entity_id?: string | null;
  created_by?: string | null;
  created_at: string;
  person?: Person;
}

export interface PersonLocation {
  id: string;
  // Optional: the detail embeds (`PersonDetailWire`) select these rows without
  // their parent id / timestamps, and new form rows have neither yet.
  person_id?: string;
  gis_location_id: string;
  location_type: 'home' | 'work' | 'mailing' | 'emergency' | 'other';
  is_primary: boolean;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  gis_location?: {
    id: string;
    formatted_address: string;
    street_number?: string | null;
    street_name?: string | null;
    city?: string | null;
    state_province?: string | null;
    postal_code?: string | null;
    country?: string | null;
    country_code?: string | null;
    latitude: number;
    longitude: number;
    confidence_score?: number | null;
    is_validated?: boolean | null;
  };
}

// Form interfaces
export interface CreatePersonData {
  // Optional — generated by the `generate_person_code` DB trigger when omitted.
  // Callers may supply one (e.g. to migrate an existing code).
  code?: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  preferred_name?: string;
  display_name?: string;
  email?: string;
  secondary_email?: string;
  phone?: string;
  mobile_phone?: string;
  work_phone?: string;
  company?: string;
  job_title?: string;
  department?: string;
  person_type?: string[];
  is_business?: boolean;
  has_system_access?: boolean;
  profile_photo_url?: string;
  linkedin_url?: string;
  website?: string;
  bio?: string;
  notes?: string;
  tags?: string[];
  status?: string;
  disciplines?: Array<{
    discipline_id: string;
    context_type?: string;
    context_id?: string;
    start_date?: string;
    is_primary?: boolean;
    notes?: string;
  }>;
  skills?: Array<{
    skill_id: string;
    proficiency_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    years_experience?: number;
    last_used?: string;
    notes?: string;
  }>;
  emergency_contacts?: Array<{
    contact_name: string;
    relationship?: string;
    phone?: string;
    email?: string;
    is_primary?: boolean;
  }>;
  locations?: Array<{
    gis_location_id: string;
    location_type: 'home' | 'work' | 'mailing' | 'emergency' | 'other';
    is_primary?: boolean;
    notes?: string;
  }>;
}

export interface UpdatePersonData extends Partial<CreatePersonData> {
  id: string;
}

export interface CreateDisciplineData {
  name: string;
  description?: string;
  category?: string;
  color?: string;
  icon?: string;
}

export interface CreateSkillData {
  name: string;
  category?: string;
  description?: string;
  color?: string;
}

export interface PersonCreateDEIData {
  person_id: string;
  organization_id: string;
  gender?: string;
  pronouns?: string;
  ethnicity?: string[];
  race?: string[];
  sexual_orientation?: string;
  disability_status?: string;
  veteran_status?: string;
  age_range?: string;
  primary_language?: string;
  religious_affiliation?: string;
  socioeconomic_background?: string;
  /**
   * `timestamptz` survey stamp. PUT replaces the whole row, so a save that
   * should keep the stored stamp must send it back; `undefined` leaves it
   * out of the body (the row's value becomes NULL).
   */
  dei_survey_completed_at?: string | null;
  dei_data_consent: boolean;
}

// Filter and search interfaces
export interface PersonFilters {
  search?: string;
  person_type?: string[];
  disciplines?: string[];
  skills?: string[];
  status?: string[];
  company?: string;
  department?: string;
  tags?: string[];
}

/**
 * The full person the detail page, the edit form and the tabs work with —
 * exactly what `GET /api/v1/people/orgs/:orgId/people/by-code/:code` returns
 * (see `PersonDetailWire` in `@releve/shared`). Kept under its old name so the
 * web's existing importers compile unchanged.
 */
export type PersonWithRelations = PersonDetailWire;

// Proficiency levels (the `person_skills.proficiency_level` CHECK)
export const PROFICIENCY_LEVELS = PERSON_PROFICIENCY_LEVELS;

export type ProficiencyLevel = typeof PROFICIENCY_LEVELS[number];

// Discipline categories
export const DISCIPLINE_CATEGORIES = [
  'internal',
  'external',
  'project',
  'business',
  'leadership'
] as const;

export type DisciplineCategory = typeof DISCIPLINE_CATEGORIES[number];

// Skill categories for art festival team organization
export const SKILL_CATEGORIES = [
  'creative_arts',
  'music_performance',
  'technical_production',
  'business_administration',
  'marketing_communications',
  'operations_logistics',
  'volunteer_coordination',
  'health_safety',
  'food_beverage',
  'business_management'
] as const;

export type SkillCategory = typeof SKILL_CATEGORIES[number];

// Interaction types
export const INTERACTION_TYPES = [
  'email',
  'call',
  'meeting',
  'note',
  'task',
  'follow_up'
] as const;

export type InteractionType = typeof INTERACTION_TYPES[number];

// Location types (the `person_locations.location_type` CHECK)
export const LOCATION_TYPES = PERSON_LOCATION_TYPES;

export type LocationType = typeof LOCATION_TYPES[number];

// DEI (Diversity, Equity, and Inclusion) Constants
export const GENDER_OPTIONS = [
  'Female',
  'Male',
  'Non-binary',
  'Genderfluid',
  'Agender',
  'Two-Spirit',
  'Other',
  'Prefer not to answer'
] as const;

export const PRONOUN_OPTIONS = [
  'he/him',
  'she/her',
  'they/them',
  'xe/xir',
  'ze/zir',
  'Other',
  'Prefer not to answer'
] as const;

export const ETHNICITY_OPTIONS = [
  'Hispanic or Latino',
  'Not Hispanic or Latino',
  'Prefer not to answer'
] as const;

export const RACE_OPTIONS = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Native Hawaiian or Other Pacific Islander',
  'White',
  'Middle Eastern or North African',
  'Other',
  'Prefer not to answer'
] as const;

export const SEXUAL_ORIENTATION_OPTIONS = [
  'Heterosexual',
  'Gay',
  'Lesbian',
  'Bisexual',
  'Pansexual',
  'Asexual',
  'Queer',
  'Questioning',
  'Other',
  'Prefer not to answer'
] as const;

export const DISABILITY_STATUS_OPTIONS = [
  'Yes',
  'No',
  'Prefer not to answer'
] as const;

export const VETERAN_STATUS_OPTIONS = [
  'Veteran',
  'Not a veteran',
  'Prefer not to answer'
] as const;

export const AGE_RANGE_OPTIONS = [
  'Under 18',
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+',
  'Prefer not to answer'
] as const;

export const SOCIOECONOMIC_BACKGROUND_OPTIONS = [
  'Working class',
  'Lower middle class',
  'Middle class',
  'Upper middle class',
  'Upper class',
  'First-generation college graduate',
  'Prefer not to answer'
] as const;

export type GenderOption = typeof GENDER_OPTIONS[number];
export type PronounOption = typeof PRONOUN_OPTIONS[number];
export type EthnicityOption = typeof ETHNICITY_OPTIONS[number];
export type RaceOption = typeof RACE_OPTIONS[number];
export type SexualOrientationOption = typeof SEXUAL_ORIENTATION_OPTIONS[number];
export type DisabilityStatusOption = typeof DISABILITY_STATUS_OPTIONS[number];
export type VeteranStatusOption = typeof VETERAN_STATUS_OPTIONS[number];
export type AgeRangeOption = typeof AGE_RANGE_OPTIONS[number];
export type SocioeconomicBackgroundOption = typeof SOCIOECONOMIC_BACKGROUND_OPTIONS[number];
